import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { WindowRef } from '@core/window-ref/window-ref.service';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { Logger } from '@shared/logger';
import { filter, interval, take } from 'rxjs';

@Injectable()
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly translateService = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);
  private readonly windowRef = inject(WindowRef);
  private readonly logger: Logger = Logger.getLogger('AppUpdateService');

  init(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then(() => this.logger.debug('checking for updates'));
      interval(60 * 1000).subscribe(() =>
        this.swUpdate.checkForUpdate().then(() => this.logger.debug('checking for updates')),
      );
    }
  }

  checkForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.openConfirmationModal();
      });
  }

  private openConfirmationModal(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      hasBackdrop: true,
      closeOnNavigation: false,
      disableClose: true,
      data: {
        title: this.translateService.translate('newVersion.title'),
        message: this.translateService.translate('newVersion.text'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.windowRef.nativeWindow.location.reload();
        }
      });
  }
}
